import { useCallback } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { getOptimizelyExperimentVariation, getOptimizelyFeatureFlagVariation, isFeatureFlagExperiment } from 'lib/optimizely/optimizelyUtils'
import { OptimizelyExperiments, OptimizelyFeatureFlags } from 'constants/optimizely'
import { triggerAnalytics, hasAnalyticsTriggered } from './deferredAnalyticsMap'

type OptimizelyId = OptimizelyExperiments | OptimizelyFeatureFlags

/**
 * Optimizely experiment/feature-flag with deferred analytics activation.
 *
 * Returns the Optimizely decision immediately but only
 * triggers analytics/activation when explicitly triggered via triggerAnalytics() call.
 *
 * Behavior:
 * - variation: the Optimizely decision
 * - analyticsTriggered: whether analytics have been triggered for this flag
 * - triggerAnalytics(): triggers analytics activation for this flag
 *
 * Typical usage:
 *   const { variation, triggerAnalytics } = useDeferredAnalyticsOptimizely(OptimizelyExperiments.foo)
 *   const enabled = !!variation
 *   const onClick = () => { triggerAnalytics(); if (enabled) doThing() }
 *
 * Notes:
 * - Flag decision is available immediately (evaluated in SSR)
 * - Analytics activation is deferred until triggerAnalytics() is called
 * - Supports both experiments and feature flags
 *
 * @param id Optimizely experiment or feature flag id (rule key)
 * @returns { variation, analyticsTriggered, triggerAnalytics }
 */
export function useDeferredAnalyticsOptimizely(id: OptimizelyId) {
  const analyticsTriggered = hasAnalyticsTriggered(id)

  const variation = useAppSelector(state =>
    isFeatureFlagExperiment(id)
      ? getOptimizelyFeatureFlagVariation(state, id)
      : getOptimizelyExperimentVariation(state, id),
  )

  // Only trigger analytics when explicitly triggered
  useOptimizelyExperiment(id, analyticsTriggered)

  const memoisedTriggerAnalytics = useCallback(() => {
    triggerAnalytics(id, true)
  }, [id])

  return {
    variation,
    analyticsTriggered,
    triggerAnalytics: memoisedTriggerAnalytics,
  } as const
}
